import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import { Form } from '../../../../../../../components/Form';
import { FormField } from '../../../../../../../components/Form/FormField';
import { FormSubmitButton } from '../../../../../../../components/Form/FormSubmitButton';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import {
  FlexContainer,
  FlexContainerVertical,
  FlexItem
} from '../../../../../../../layout/Flex';
import { Section } from '../../../../../../../layout/Section';
import {
  callFirebaseFunction,
  callFirebaseFunctionViaHttp
} from '../../../../../../../services/firebaseFunctions';
import { Json } from '../../../../../../components/Json';

type Props = {
  org: Doc<IOrganization>;
};

export interface WorkdayReportData {
  endpointName: string;
  date: string;
  data?: any;
  mappingPaths?: string[];
  error?: any;
}

export interface GetWorkdayDataResponse {
  success: boolean;
  reports: WorkdayReportData[];
  timestamp: string;
}

interface WorkdayEndpoint {
  id: string;
  name: string;
  url: string;
  username: string;
}

interface WorkdayConfigResponse {
  orgId: string;
  endpoints: WorkdayEndpoint[];
}

const WorkdayDataDialog = ({
  open,
  onClose,
  org
}: {
  open: boolean;
  onClose: () => void;
  org: Doc<IOrganization>;
}) => {
  const [config, setConfig] = useState<WorkdayConfigResponse | null>(null);
  const [selectedEndpointId, setSelectedEndpointId] = useState<string>('');
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [result, setResult] = useState<GetWorkdayDataResponse | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Load config when dialog opens
  useEffect(() => {
    if (open && !config) {
      loadWorkdayConfig();
    }
  }, [open]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setConfig(null);
      setSelectedEndpointId('');
      setResult(null);
    }
  }, [open]);

  const loadWorkdayConfig = async () => {
    setLoadingConfig(true);
    try {
      const configResult = await callFirebaseFunction<WorkdayConfigResponse>(
        'integrations_workday-getWorkdayConfig',
        { orgId: org.id }
      );
      setConfig(configResult);
    } catch (error: any) {
      console.error('Error loading Workday config:', error);
      enqueueSnackbar(
        error?.message || 'Failed to load Workday configuration',
        {
          variant: 'error',
          style: { fontWeight: 500 }
        }
      );
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleFetchEndpointData = async () => {
    if (!selectedEndpointId) return;

    try {
      // onRequest (not a callable) so it can chunked-stream a report larger than
      // the callable ~32MB cap; authed with the admin's Firebase ID token. fetch
      // waits while the server streams — no client-side timeout to trip.
      const result = await callFirebaseFunctionViaHttp<GetWorkdayDataResponse>(
        'workday_getRawWorkdayData',
        {
          orgId: org.id,
          endpointId: selectedEndpointId
        }
      );
      setResult(result);

      if (result?.reports?.length) {
        const report = result.reports[0];

        if (report.data && !report.error) {
          const dataBlob = new Blob([JSON.stringify(report.data, null, 2)], {
            type: 'application/json'
          });
          const dataUrl = URL.createObjectURL(dataBlob);
          const dataLink = document.createElement('a');
          dataLink.href = dataUrl;
          dataLink.download = `${report.endpointName}-${report.date}.json`;
          dataLink.click();
          URL.revokeObjectURL(dataUrl);

          if (report.mappingPaths) {
            const mappingBlob = new Blob([JSON.stringify(report.mappingPaths, null, 2)], {
              type: 'application/json'
            });
            const mappingUrl = URL.createObjectURL(mappingBlob);
            const mappingLink = document.createElement('a');
            mappingLink.href = mappingUrl;
            mappingLink.download = `${report.endpointName}-${report.date}-mappings.json`;
            mappingLink.click();
            URL.revokeObjectURL(mappingUrl);
          }

          enqueueSnackbar(
            `Successfully downloaded data for ${report.endpointName}`,
            {
              variant: 'success',
              style: { fontWeight: 500 }
            }
          );
        }

        if (report.error) {
          console.warn(`WORKDAY REPORT ERROR: ${report.endpointName}`, report.error);
          enqueueSnackbar(
            `The Workday ${report.endpointName} report has an error - check the console for details`,
            {
              variant: 'warning',
              style: { fontWeight: 500 }
            }
          );
        }
      }
    } catch (error: any) {
      console.error('Error fetching Workday endpoint data:', error);
      enqueueSnackbar(
        error?.message || 'Something went wrong - check console for details',
        {
          variant: 'error',
          style: { fontWeight: 500 }
        }
      );
    }
  };

  const selectedEndpoint = config?.endpoints.find(e => e.id === selectedEndpointId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Download Workday Data</DialogTitle>
      <DialogContent>
        <FlexContainerVertical fullWidth spacing={3}>
          {loadingConfig && (
            <FlexContainer justifyContent="center" alignItems="center">
              <CircularProgress size={24} />
              <Typography variant="body2" style={{ marginLeft: 16 }}>
                Loading Workday configuration...
              </Typography>
            </FlexContainer>
          )}

          {!loadingConfig && config && (
            <>
              <FormControl fullWidth>
                <InputLabel>Select Workday Endpoint</InputLabel>
                <Select
                  value={selectedEndpointId}
                  onChange={(e) => setSelectedEndpointId(e.target.value as string)}
                >
                  {config.endpoints.map((endpoint) => (
                    <MenuItem key={endpoint.id} value={endpoint.id}>
                      {endpoint.name} ({endpoint.username})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedEndpoint && (
                <Card style={{ padding: 16, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Endpoint Details:
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {selectedEndpoint.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>URL:</strong> {selectedEndpoint.url}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Username:</strong> {selectedEndpoint.username}
                  </Typography>
                </Card>
              )}

              <FlexContainer justifyContent="center">
                <ButtonWithPromise
                  variant="contained"
                  color="primary"
                  disabled={!selectedEndpointId}
                  onClick={handleFetchEndpointData}
                  pending="Fetching Workday data..."
                >
                  Download Data for Selected Endpoint
                </ButtonWithPromise>
              </FlexContainer>

              {result && (
                <Card style={{ padding: 16, backgroundColor: '#e8f5e8' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Download Result:
                  </Typography>
                  <Typography variant="body2">
                    <strong>Success:</strong> {result.success ? 'Yes' : 'No'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Timestamp:</strong> {result.timestamp}
                  </Typography>
                  {result.reports[0]?.data && (
                    <Typography variant="body2">
                      <strong>Records:</strong> {result.reports[0].data?.Report_Entry?.length || 0}
                    </Typography>
                  )}
                </Card>
              )}
            </>
          )}

          {!loadingConfig && !config && (
            <FlexContainer justifyContent="center">
              <Button
                variant="outlined"
                color="primary"
                onClick={loadWorkdayConfig}
              >
                Retry Loading Configuration
              </Button>
            </FlexContainer>
          )}
        </FlexContainerVertical>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const MsFileInfoDialog = ({
  open,
  onClose,
  org
}: {
  open: boolean;
  onClose: () => void;
  org: Doc<IOrganization>;
}) => {
  const [res, setRes] = useState<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Get MS File Item Info by Web URL</DialogTitle>
      <DialogContent>
        <FlexContainerVertical fullWidth spacing={3}>
          <Form
            style={{ width: '100%' }}
            initalValues={{ url: '' }}
            onSubmit={(values) =>
              callFirebaseFunction(
                'integrations_microsoft-getDriveItemInfoFromWebUrl',
                {
                  webUrl: values.url,
                  orgId: org.id
                }
              )
                .then((res) => setRes(res))
                .catch((err) => {
                  console.error('MS FILE ITEM INFO ERROR', err);
                  enqueueSnackbar(
                    'Something went wrong - check console for details',
                    { variant: 'error' }
                  );
                })
            }
          >
            {({ submitting, values }) => (
              <FlexContainer fullWidth>
                <FlexItem flex={1}>
                  <FormField<string> name="url">
                    {({ input }) => (
                      <TextField {...input} fullWidth autoFocus />
                    )}
                  </FormField>
                </FlexItem>
                <FormSubmitButton
                  submitComponent="Checking..."
                  submitting={submitting}
                  variant="contained"
                  disabled={!values.url || !values.url.startsWith('http')}
                  color="primary"
                >
                  Check
                </FormSubmitButton>
              </FlexContainer>
            )}
          </Form>

          {res && <Json data={res} />}
        </FlexContainerVertical>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const ExcelFileInfoDialog = ({
  open,
  onClose,
  org
}: {
  open: boolean;
  onClose: () => void;
  org: Doc<IOrganization>;
}) => {
  const [res, setRes] = useState<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Get MS Excel file info by Web URL</DialogTitle>
      <DialogContent>
        <FlexContainerVertical fullWidth spacing={3}>
          <Form
            style={{ width: '100%' }}
            initalValues={{ url: '' }}
            onSubmit={(values) =>
              callFirebaseFunction(
                'integrations_microsoft-getExcelFileInfoFromWebUrl',
                {
                  webUrl: values.url,
                  orgId: org.id
                }
              )
                .then((res) => setRes(res))
                .catch((err) => {
                  console.error('MS EXCEL INFO ERROR', err);
                  enqueueSnackbar(
                    'Something went wrong - check console for details',
                    { variant: 'error' }
                  );
                })
            }
          >
            {({ submitting, values }) => (
              <FlexContainer fullWidth>
                <FlexItem flex={1}>
                  <FormField<string> name="url">
                    {({ input }) => (
                      <TextField {...input} fullWidth autoFocus />
                    )}
                  </FormField>
                </FlexItem>
                <FormSubmitButton
                  submitComponent="Checking..."
                  submitting={submitting}
                  variant="contained"
                  disabled={!values.url || !values.url.startsWith('http')}
                  color="primary"
                >
                  Check
                </FormSubmitButton>
              </FlexContainer>
            )}
          </Form>

          {res && <Json data={res} />}
        </FlexContainerVertical>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export const IntegrationSupport: React.FC<Props> = ({ org }) => {
  const excelDialog = useDialogState();
  const msFileInfoDialog = useDialogState();
  const workdayDialog = useDialogState();

  return (
    <Section>
      <CanvasBar>
        <div>Integration support</div>
      </CanvasBar>

      <Card>
        <FlexContainer wrap="wrap">
          <Button
            variant="contained"
            color="primary"
            onClick={workdayDialog.openDialog}
          >
            Download Workday Data
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={excelDialog.openDialog}
          >
            Check MS Excel Web URL
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={msFileInfoDialog.openDialog}
          >
            Check MS File Info for Web URL
          </Button>
        </FlexContainer>
      </Card>

      <ExcelFileInfoDialog
        org={org}
        open={excelDialog.dialogOpen}
        onClose={excelDialog.closeDialog}
      />
      <MsFileInfoDialog
        org={org}
        open={msFileInfoDialog.dialogOpen}
        onClose={msFileInfoDialog.closeDialog}
      />
      <WorkdayDataDialog
        org={org}
        open={workdayDialog.dialogOpen}
        onClose={workdayDialog.closeDialog}
      />
    </Section>
  );
};
